import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { Modal } from '../../custom-essentials';
import { checkResponse } from '../../form';
import { formatDate, formatDateApi, processRelInfo } from '../../functions';
import DetailsModalBFReading from './DetailsModalBFReading';

import {
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchAppointmentsIds,
    fetchStudentsUserIds,
    fetchRelevantInfoMemberDaterangeStudentName,
    fetchBookListsStudents,
    fetchBooksAll,
    updateAppointmentDetails,
    updateBookList,
    createFlag,
} from '../../../actions';

function DetailsModalReading(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [attemptingClose, setAttemptingClose] = useState(false);
    const [showModal, setShowModal] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [closureSubmitting, setClosureSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState({ errored: false, completed: false });
    const [oneSuccess, setOneSuccess] = useState(false);
    const [instructorOptions, setInstructorOptions] = useState([]);
    const [centers, setCenters] = useState([]);
    const [relevantAssignments, setRelevantAssignments] = useState([]);
    const [appointment, setAppointment] = useState({});
    const [relInfo, setRelInfo] = useState({});
    const [bookListOptions, setBookListOptions] = useState([]);

    const { selectedAppointment, onSubmitCallback, fetchRpCentersAll, fetchRelevantInfoMemberDaterangeStudentName,
        fetchBookListsStudents, fetchBooksAll, fetchAdminUsersAll,
        fetchAppointmentsIds, fetchStudentsUserIds,
        updateAppointmentDetails, updateBookList, createFlag } = props;

    useEffect(() => {
        async function init() {
            // Fetch ReadingPlex centers
            const centerRes = await fetchRpCentersAll();
            const appointmentRes = await fetchAppointmentsIds({ ids: [selectedAppointment.id] });
            const instructorsRes = await fetchAdminUsersAll();
            const studentRes = await fetchStudentsUserIds({ userIds: [selectedAppointment.student] });
            // Fetch book lists for this student and all books for title lookup
            let bookListsStudentRes = { data: { bookLists: [], bookListItems: [] } };
            let booksRes = { data: [] };
            try {
                [bookListsStudentRes, booksRes] = await Promise.all([
                    fetchBookListsStudents({ studentIds: [selectedAppointment.student] }),
                    fetchBooksAll()
                ]);
            } catch (e) {
                console.log('[DetailsModalReading] Error fetching book lists or books:', e);
            }

            const newCenters = centerRes.data || [];
            const newAppointment = appointmentRes.data?.appointments?.[0] || {};
            const newRelevantAssignments = appointmentRes.data?.assignments || [];
            const newInstructors = instructorsRes.data || [];
            const newStudent = studentRes.data?.[0] || {};
            const newBookLists = bookListsStudentRes.data?.bookLists || [];
            const newBookListItems = bookListsStudentRes.data?.bookListItems || [];
            const newBooks = booksRes.data || [];

            // Create a book ID to title map
            const bookMap = {};
            newBooks.forEach(b => {
                bookMap[parseInt(b.id)] = b.title || `Book ${b.id}`;
            });

            const studentName = `${newStudent.first_name || ''} ${newStudent.last_name || ''}`.trim();

            // Filter instructors to those with RP permissions or assigned to this appointment
            newInstructors.forEach(i => i.fullName = `${i.first_name} ${i.last_name}`);
            const newInstructorOptions = newInstructors.filter(i => {
                return (newRelevantAssignments.some(a => a.instructor === i.id) ||
                    (i.rp_permissions !== 'None' && parseInt(i.account_active) === 1));
            }).sort((a, b) => {
                if (a.fullName < b.fullName) return -1;
                else if (a.fullName > b.fullName) return 1;
                else return 0;
            }).map(i => ({ value: i.id, label: i.fullName }));

            const centerMap = {};
            newCenters.forEach(c => centerMap[c.id] = c.name);
            newAppointment.centerName = centerMap[parseInt(newAppointment.center || -1)] || `Unknown center (ID: ${newAppointment.center})`;
            newAppointment.studentName = studentName;
            newAppointment.studentObject = newStudent;
            const aptDateTime = new Date(newAppointment.date_time);
            newAppointment.startTime = aptDateTime.getHours() * 60 + aptDateTime.getMinutes() * 1;
            newAppointment.endTime = newAppointment.startTime + parseInt(newAppointment.duration);

            // Fetch relevant info (past appointments, flags, etc.)
            const startDate = new Date();
            startDate.setMonth(startDate.getMonth() - 3);
            const endDate = new Date();
            const relevantInfoRes = await fetchRelevantInfoMemberDaterangeStudentName({
                startDate: formatDateApi(startDate),
                endDate: formatDateApi(endDate),
                searchTerm: studentName
            });
            const newRelInfo = relevantInfoRes.data || {};
            const processedRelInfo = processRelInfo(newRelInfo, newCenters, newInstructors, studentName);

            // Create book list options with their items, enriched with book titles
            const bookListItemMap = {};
            newBookListItems.forEach(bli => {
                const listId = parseInt(bli.book_list_id);
                if (!bookListItemMap[listId]) bookListItemMap[listId] = [];
                // Enrich book list item with book title
                bli.book_name = bookMap[parseInt(bli.book_id)] || `Book ID: ${bli.book_id}`;
                bookListItemMap[listId].push(bli);
            });

            newBookLists.forEach(bl => {
                bl.items = bookListItemMap[parseInt(bl.id)] || [];
            });

            const newBookListOptions = newBookLists.length ? newBookLists.map(bl => {
                return { value: parseInt(bl.id), label: `${bl.name} (${formatDate(bl.date_created)})`, obj: bl };
            }) : [{ value: -1, label: 'No book lists found', obj: {} }];

            if (mounted.current) {
                setCenters(newCenters);
                setInstructorOptions(newInstructorOptions);
                setRelevantAssignments(newRelevantAssignments);
                setAppointment(newAppointment);
                setRelInfo(processedRelInfo);
                setBookListOptions(newBookListOptions);
                setLoaded(true);
            }
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback((changes, forceClose = false) => {
        if (!forceClose) {
            if (changes !== true) changes = false;
            if (closureSubmitting) return;
            if (!attemptingClose) {
                setAttemptingClose(true);
                return;
            }
        }
        (async function close() {
            await onSubmitCallback(changes || oneSuccess);
            if (mounted.current) setShowModal(false);
        })();
    }, [onSubmitCallback, attemptingClose, closureSubmitting, oneSuccess]);

    const handleSubmit = useCallback((values, actions) => {
        async function submit() {
            const { setStatus, setSubmitting } = actions;

            if (mounted.current) {
                setSubmitting(true);
                setClosureSubmitting(true);
            }

            // PREPARE DATA
            const detailsParams = {
                id: appointment.id,
                instructors: values.instructors.map(si => si.value),
                status: values.status.value,
                participation: values.participation?.value || 0,
                answerAccuracy: values.answerAccuracy?.value || 0,
                depthOfThinking: values.depthOfThinking?.value || 0,
                vocabPages: values.vocabPages || '',
                sessionNotes: values.sessionNotes || '',
                homeworkNotes: values.hwNotes || '',
                internalNotes: values.internalNotes || '',
                stampsGiven: values.stampsGiven ? 1 : 0,
                specialNotesSchedule: values.specialNotesSchedule || '',
            };

            // Update book list items if any were modified
            const selectedBookList = values.selectedBookList?.obj;
            if (selectedBookList && selectedBookList.items && selectedBookList.items.length > 0) {
                // Find the book list ID
                const bookListId = selectedBookList.id;
                if (bookListId) {
                    // Update each book list item that has a date_completed change
                    const bookListItemsToUpdate = selectedBookList.items.map(item => ({
                        book_id: item.book_id,
                        date_assigned: item.date_assigned || null,
                        date_completed: item.date_completed || null,
                        sort_order: item.sort_order || 0
                    }));

                    // Update book list items via API
                    try {
                        await updateBookList({
                            id: bookListId,
                            items: JSON.stringify(bookListItemsToUpdate)
                        });
                    } catch (error) {
                        console.error('[APPOINTMENT DETAILS] Error updating book list items:', error);
                    }
                }
            }

            const flagsParams = {
                student: appointment.student,
                tag: values.flagTag.value,
                center: values.flagCenter.value,
                additionalNotes: values.flagNotes,
                adminNotes: '',
                status: 'Pending'
            };

            // SEND DATA
            const nss = { errored: false, completed: false };
            nss.appointmentDetails = { name: 'Appointment Details', completed: false, message: '' };
            if (values.createFlag) nss.flag = { name: 'Create Flag', completed: false, message: '' };
            setSubmissionStatus({ ...nss });

            const uadResponse = await updateAppointmentDetails(detailsParams);
            nss.appointmentDetails.completed = true;
            nss.errored = !checkResponse(uadResponse, mounted, (resp) => nss.appointmentDetails.message = resp) || nss.errored;
            setSubmissionStatus({ ...nss });

            if (values.createFlag) {
                const cfResponse = await createFlag(flagsParams);
                nss.flag.completed = true;
                nss.errored = !checkResponse(cfResponse, mounted, (resp) => nss.flag.message = resp) || nss.errored;
                setSubmissionStatus({ ...nss });
            }

            setOneSuccess(oneSuccess || Object.values(nss).some(s => s.message === 'Success!'));

            nss.completed = true;

            if (nss.errored && mounted.current) {
                setClosureSubmitting(false);
                setStatus(`One or more errors occurred during submission.`);
                setSubmissionStatus(nss);
                return;
            }

            if (mounted.current) {
                setClosureSubmitting(false);
                setSubmitted(true);
                setSubmitting(false);
            }
            setTimeout(() => handleClose(true, true), 1000);
        }
        submit();
    }, [handleClose, appointment, updateAppointmentDetails, updateBookList, createFlag, oneSuccess]);

    return (
        <Modal className="w-9/12" show={showModal} onHide={handleClose}>
            <Modal.Header>
                <h2>Reading Appointment Details</h2>
            </Modal.Header>
            <DetailsModalBFReading
                loaded={loaded}
                attemptingClose={attemptingClose}
                setAttemptingClose={setAttemptingClose}
                centers={centers}
                selectedAppointment={appointment}
                instructorOptions={instructorOptions}
                relevantAssignments={relevantAssignments}
                relInfo={relInfo}
                bookListOptions={bookListOptions}
                submitted={submitted}
                submissionStatus={submissionStatus}
                handleClose={handleClose}
                handleSubmit={handleSubmit}
            />
        </Modal>
    );
}

export default connect(null, {
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchAppointmentsIds,
    fetchStudentsUserIds,
    fetchRelevantInfoMemberDaterangeStudentName,
    fetchBookListsStudents,
    fetchBooksAll,
    updateAppointmentDetails,
    updateBookList,
    createFlag,
})(DetailsModalReading);

